import { createEnv } from "@t3-oss/env-nextjs";
import { z } from "zod";

import { discordSchema } from "@sorcery/utils/server/discord/discord-schema";
import { redisSchema } from "@sorcery/utils/server/redis/redis-schema";
import { sanitySchema } from "@sorcery/utils/server/sanity/sanity-schema";

export const env = createEnv({
  shared: {
    VERCEL_URL: z
      .string()
      .optional()
      .transform((v) => (v ? `https://${v}` : undefined)),
    PORT: z.coerce.number().default(3001),
  },
  /**
   * Specify your server-side environment variables schema here. This way you can ensure the app isn't
   * built with invalid env vars.
   */
  server: {
    ...discordSchema.shape,
    ...redisSchema.shape,
    ...sanitySchema.shape,
    DATABASE_URL: z.string().url(),
    NODE_ENV: z.enum(["development", "test", "production"]),
    VERCEL_ENV: z.enum(["development", "preview", "production"]),
    AUTH0_SECRET: z.string(),
    AUTH0_BASE_URL: z.string(),
    AUTH0_ISSUER_BASE_URL: z.string().url(),
    AUTH0_CLIENT_ID: z.string(),
    AUTH0_CLIENT_SECRET: z.string(),
    KNOCK_SIGNING_KEY: z.string(),
    KNOCK_PRIVATE_KEY: z.string(),
    CDN_DOMAIN: z.string().optional(),
  },
  /**
   * Specify your client-side environment variables schema here.
   * For them to be exposed to the client, prefix them with `NEXT_PUBLIC_`.
   */
  client: {
    NEXT_PUBLIC_OFFLINE: z.string(),
    NEXT_PUBLIC_KNOCK_PUBLIC_KEY: z.string(),
    NEXT_PUBLIC_VERCEL_ENV: z.enum(["development", "preview", "production"]),
    NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA: z.string().optional(),
    NEXT_PUBLIC_DEV_KEY: z.string().optional(),
    NEXT_PUBLIC_SANITY_PROJECT_ID: z.string(),
    NEXT_PUBLIC_SANITY_DATASET: z.string(),
  },
  /**
   * Destructure all variables from `process.env` to make sure they aren't tree-shaken away.
   */
  runtimeEnv: {
    VERCEL_URL: process.env.VERCEL_URL,
    PORT: process.env.PORT,
    ...(Object.keys(discordSchema.shape) as (keyof typeof discordSchema.shape)[]).reduce(
      (acc, key) => {
        acc[key] = process.env[key];
        return acc;
      },
      {} as Record<keyof typeof discordSchema.shape, string | undefined>,
    ),
    ...(Object.keys(redisSchema.shape) as (keyof typeof redisSchema.shape)[]).reduce(
      (acc, key) => {
        acc[key] = process.env[key];
        return acc;
      },
      {} as Record<keyof typeof redisSchema.shape, string | undefined>,
    ),
    ...(Object.keys(sanitySchema.shape) as (keyof typeof sanitySchema.shape)[]).reduce(
      (acc, key) => {
        acc[key] = process.env[key];
        return acc;
      },
      {} as Record<keyof typeof sanitySchema.shape, string | undefined>,
    ),
    DATABASE_URL: process.env.DATABASE_URL,
    NODE_ENV: process.env.NODE_ENV,
    VERCEL_ENV: process.env.VERCEL_ENV,
    AUTH0_SECRET: process.env.AUTH0_SECRET,
    AUTH0_BASE_URL: process.env.AUTH0_BASE_URL,
    AUTH0_ISSUER_BASE_URL: process.env.AUTH0_ISSUER_BASE_URL,
    AUTH0_CLIENT_ID: process.env.AUTH0_CLIENT_ID,
    AUTH0_CLIENT_SECRET: process.env.AUTH0_CLIENT_SECRET,
    KNOCK_SIGNING_KEY: process.env.KNOCK_SIGNING_KEY,
    KNOCK_PRIVATE_KEY: process.env.KNOCK_PRIVATE_KEY,
    CDN_DOMAIN: process.env.CDN_DOMAIN,

    NEXT_PUBLIC_OFFLINE: process.env.NEXT_PUBLIC_OFFLINE,
    NEXT_PUBLIC_KNOCK_PUBLIC_KEY: process.env.NEXT_PUBLIC_KNOCK_PUBLIC_KEY,
    NEXT_PUBLIC_VERCEL_ENV: process.env.NEXT_PUBLIC_VERCEL_ENV,
    NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
    NEXT_PUBLIC_DEV_KEY: process.env.NEXT_PUBLIC_DEV_KEY,
    NEXT_PUBLIC_SANITY_PROJECT_ID: process.env.NEXT_PUBLIC_SANITY_PROJECT_ID,
    NEXT_PUBLIC_SANITY_DATASET: process.env.NEXT_PUBLIC_SANITY_DATASET,
  },
  skipValidation: process.env.npm_lifecycle_event === "lint",
});
